// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useRef, useState } from 'react';
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
} from 'react-native';
import Markdown from 'react-native-markdown-display';

import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import { Text, View } from '@/components/Themed';
import { RefereeMessage, useReferee } from '@/features/referee/useReferee';
import MicButton from '@/components/MicButton';
import { useI18n } from '@/i18n';

import { answerMarkdownStyles, styles } from './styles';

/** The examples shown on an empty conversation — one of each kind the referee handles. */
const SUGGESTION_KEYS = ['referee.suggestion1', 'referee.suggestion2', 'referee.suggestion3'] as const;

export default function RefereeScreen() {
  const { t } = useI18n();
  const {
    messages,
    conversations,
    asking,
    errorCode,
    ask,
    startNewConversation,
    openConversation,
  } = useReferee();

  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Dictation lands in the draft, never straight at the referee — the player reads it back and
  // fixes "Stableford" before spending a question on it.
  const [speechError, setSpeechError] = useState<string | null>(null);
  const listRef = useRef<FlatList<RefereeMessage>>(null);
  const inputRef = useRef<TextInput>(null);

  const submit = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || asking) return;
      ask(trimmed);
      setDraft('');
      setShowHistory(false);
    },
    [ask, asking]
  );

  const renderMessage = useCallback(
    ({ item }: { item: RefereeMessage }) => {
      if (item.role === 'user') {
        return (
          <View style={[styles.bubble, styles.playerBubble]}>
            <Text style={styles.playerText}>{item.content}</Text>
          </View>
        );
      }

      return (
        <View style={[styles.bubble, styles.refereeBubble]} testID="referee-answer">
          {item.content ? (
            <Markdown style={answerMarkdownStyles}>{item.content}</Markdown>
          ) : (
            <Text style={styles.thinking}>{t('referee.thinking')}</Text>
          )}
        </View>
      );
    },
    [t]
  );

  return (
    <KeyboardAvoidingView
      style={styles.screen}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.historyHeader}>
        <Text style={styles.historyTitle}>
          {showHistory ? t('referee.historyTitle') : ''}
        </Text>
        <View style={styles.headerActions}>
          <GoffyButton
            label={showHistory ? t('referee.hideHistory') : t('referee.showHistory')}
            size="compact"
            style={styles.autoWidthButton}
            onPress={() => setShowHistory((current) => !current)}
            testID="referee-history-toggle"
          />
          <GoffyButton
            label={t('referee.newConversation')}
            size="compact"
            style={styles.autoWidthButton}
            onPress={() => {
              startNewConversation();
              setShowHistory(false);
            }}
            testID="referee-new-conversation"
          />
        </View>
      </View>

      {showHistory ? (
        <ScrollView style={styles.list}>
          {conversations.length === 0 ? (
            <View style={styles.empty}>
              <Text style={styles.introText}>{t('referee.historyEmpty')}</Text>
            </View>
          ) : (
            conversations.map((conversation) => (
              <Pressable
                key={conversation.conversationId}
                style={styles.historyRow}
                onPress={() => {
                  openConversation(conversation.conversationId);
                  setShowHistory(false);
                }}
              >
                <Text style={styles.historyRowText} numberOfLines={2}>
                  {conversation.title}
                </Text>
              </Pressable>
            ))
          )}
        </ScrollView>
      ) : messages.length === 0 ? (
        <ScrollView style={styles.list} contentContainerStyle={styles.empty}>
          <Text style={styles.emptyTitle}>{t('referee.emptyTitle')}</Text>
          <Text style={styles.introText}>{t('referee.emptyBody')}</Text>
          {SUGGESTION_KEYS.map((key) => (
            <Pressable key={key} style={styles.suggestion} onPress={() => submit(t(key))}>
              <Text style={styles.suggestionText}>{t(key)}</Text>
            </Pressable>
          ))}
        </ScrollView>
      ) : (
        <FlatList
          ref={listRef}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          data={messages}
          keyExtractor={(item) => item.id}
          renderItem={renderMessage}
          onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: true })}
        />
      )}

      {errorCode ? (
        <Text style={styles.error} testID="referee-error">
          {refereeErrorText(errorCode, t)}
        </Text>
      ) : null}

      {speechError ? (
        <Text style={styles.error} testID="referee-mic-error">
          {speechError}
        </Text>
      ) : null}

      <View style={styles.composer}>
        <GoffyInput
          ref={inputRef}
          style={styles.input}
          value={draft}
          onChangeText={setDraft}
          placeholder={t('referee.placeholder')}
          multiline
          editable={!asking}
          onSubmitEditing={() => submit(draft)}
          testID="referee-input"
        />
        <MicButton
          value={draft}
          onChangeText={setDraft}
          disabled={asking}
          onError={setSpeechError}
          testID="referee-mic"
        />
        <GoffyButton
          label={t('referee.send')}
          size="compact"
          tone="accent"
          style={styles.autoWidthButton}
          disabled={asking || draft.trim().length === 0}
          onPress={() => submit(draft)}
          testID="referee-send"
        />
      </View>

      <Text style={styles.disclaimer}>{t('referee.disclaimer')}</Text>
    </KeyboardAvoidingView>
  );
}

/**
 * Maps a `referee.*` error code to its string, falling back to the generic one. The fallback
 * matters: the service can add a code before the app ships a translation for it.
 */
export function refereeErrorText(code: string, t: (key: string) => string): string {
  const known = [
    'referee.unauthorized',
    'referee.invalidRequest',
    'referee.offTopic',
    'referee.unavailable',
    'referee.rateLimited',
    'referee.questionTooLong',
    'referee.emptyQuestion',
    'referee.emptyAnswer',
    'referee.refused',
    'referee.notConfigured',
    'referee.providerFailed',
  ];
  return known.includes(code) ? t(`referee.errors.${code.slice('referee.'.length)}`) : t('referee.errors.unavailable');
}
